import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../environments/environment";

// Klipy API response interfaces
interface KlipyFileSizes {
  hd: { gif: { url: string }; mp4: { url: string } };
  md: { gif: { url: string }; mp4: { url: string } };
  sm: { gif: { url: string }; mp4: { url: string } };
  xs: { gif: { url: string }; mp4: { url: string } };
}

interface KlipyGifItem {
  id: number;
  slug: string;
  title: string;
  file: KlipyFileSizes;
  tags: string[];
  type: "gif" | "ad";
}

interface KlipyResponse {
  result: boolean;
  data: {
    data: KlipyGifItem[];
    meta: {
      current_page: number;
      per_page: number;
      total: number;
      total_pages: number;
    };
  };
}

@Injectable({
  providedIn: "root",
})
export class KlipyService {
  private readonly apiKey = environment.klipyApiKey;
  private readonly baseUrl = "https://api.klipy.com";

  constructor(private http: HttpClient) {}

  /**
   * Search Klipy for GIFs by keyword.
   *
   * @param query The search keyword.
   * @param page Page number (default: 1).
   * @param perPage Number of GIFs per page (default: 20).
   */
  searchGifs(
    query: string,
    page: number = 1,
    perPage: number = 20,
  ): Observable<{ gifs: string[]; hasNext: boolean }> {
    const trimmed = query.trim();
    if (!trimmed) {
      return new Observable((observer) => {
        observer.next({ gifs: [], hasNext: false });
        observer.complete();
      });
    }

    const params = new HttpParams()
      .set("q", trimmed)
      .set("page", page.toString())
      .set("per_page", perPage.toString());

    return this.http
      .get<KlipyResponse>(`${this.baseUrl}/api/v1/${this.apiKey}/gifs/search`, {
        params,
      })
      .pipe(
        map((response) => {
          const gifs: string[] = (response.data?.data || [])
            .filter((item) => item.type !== "ad") // Filter out ads
            .map((item) => item.file.sm.gif.url); // Use small GIF for preview

          const hasNext =
            response.data?.meta?.current_page < response.data?.meta?.total_pages;

          return { gifs, hasNext };
        }),
      );
  }

  /**
   * Get trending GIFs.
   *
   * @param page Page number (default: 1).
   * @param perPage Number of GIFs per page (default: 20).
   */
  getTrendingGifs(
    page: number = 1,
    perPage: number = 20,
  ): Observable<{ gifs: string[]; hasNext: boolean }> {
    const params = new HttpParams()
      .set("page", page.toString())
      .set("per_page", perPage.toString());

    return this.http
      .get<KlipyResponse>(`${this.baseUrl}/api/v1/${this.apiKey}/gifs/trending`, {
        params,
      })
      .pipe(
        map((response) => {
          const gifs: string[] = (response.data?.data || [])
            .filter((item) => item.type !== "ad") // Filter out ads
            .map((item) => item.file.sm.gif.url); // Use small GIF for preview

          const hasNext =
            response.data?.meta?.current_page < response.data?.meta?.total_pages;

          return { gifs, hasNext };
        }),
      );
  }

  /**
   * Get GIF categories.
   */
  getCategories(): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.baseUrl}/api/v1/${this.apiKey}/gifs/categories`,
    );
  }
}
